import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Target, CheckCircle, BarChart3, Award, Calendar } from 'lucide-react';

const CutTheFogPage = () => {
  const [auditEntries, setAuditEntries] = useState([]);
  const [currentEntry, setCurrentEntry] = useState({
    meeting_name: '',
    date_time: '',
    participants: '',
    purpose: '',
    decision_made: '',
    action_owner: '',
    deadline: '',
    classification: ''
  });
  const [showDashboard, setShowDashboard] = useState(false);

  const handleInputChange = (field, value) => {
    setCurrentEntry(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addAuditEntry = () => {
    if (!currentEntry.meeting_name || !currentEntry.classification) {
      alert('Please fill in Meeting Name and Classification');
      return;
    }
    
    const newEntry = {
      ...currentEntry,
      id: Date.now(),
      timestamp: new Date().toISOString()
    };
    
    setAuditEntries(prev => [...prev, newEntry]);
    
    // Reset form
    setCurrentEntry({
      meeting_name: '',
      date_time: '',
      participants: '',
      purpose: '',
      decision_made: '',
      action_owner: '',
      deadline: '',
      classification: ''
    });
  };

  const exportToPDF = () => {
    if (auditEntries.length === 0) {
      alert('Please add at least one meeting to audit before generating PDF report');
      return;
    }
    
    // Create proper PDF using browser print functionality
    const stats = getMotionMovementStats();
    const reportDate = new Date().toLocaleDateString();
    const redFlags = auditEntries.filter(e => e.classification === 'Motion' && (!e.decision_made.includes('Yes') || e.deadline === 'No deadline set'));
    
    // Create new window with formatted HTML for PDF
    const newWindow = window.open('', '_blank');
    newWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
        <title>Meeting Culture Audit Report</title>
        <style>
            body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 30px; }
            .section { margin-bottom: 30px; }
            .section h2 { color: #333; border-bottom: 1px solid #ccc; padding-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .red-flag { background-color: #ffebee; }
            .good { background-color: #e8f5e8; }
            .motion { background-color: #fff3e0; }
            .movement { background-color: #e3f2fd; }
            .stats { display: flex; justify-content: space-around; margin: 20px 0; }
            .stat-box { text-align: center; padding: 15px; border: 1px solid #ddd; border-radius: 8px; }
            .stat-number { font-size: 24px; font-weight: bold; color: #1976d2; }
        </style>
    </head>
    <body>
        <div class="header">
            <h1>MEETING CULTURE AUDIT REPORT</h1>
            <p>Generated by Smart QRM Navigator • Jan Pauwels • LEAN LEAD Winner 2026</p>
            <p>Report Date: ${reportDate} • Total Meetings: ${auditEntries.length}</p>
        </div>
        
        <div class="section">
            <h2>Executive Summary</h2>
            <div class="stats">
                <div class="stat-box">
                    <div class="stat-number">${stats.motionPercent}%</div>
                    <div>Motion Meetings</div>
                    <small>(Decision-focused)</small>
                </div>
                <div class="stat-box">
                    <div class="stat-number">${stats.movementPercent}%</div>
                    <div>Movement Meetings</div>
                    <small>(Status updates)</small>
                </div>
                <div class="stat-box">
                    <div class="stat-number">${Math.round(auditEntries.filter(e => e.decision_made.includes('Yes')).length / (auditEntries.length || 1) * 100)}%</div>
                    <div>Decision Rate</div>
                    <small>(Meetings with decisions)</small>
                </div>
            </div>
        </div>
        
        <div class="section">
            <h2>Detailed Audit Results</h2>
            <table>
                <thead>
                    <tr>
                        <th>Meeting Name</th>
                        <th>Purpose</th>
                        <th>Decision Made</th>
                        <th>Action Owner</th>
                        <th>Deadline</th>
                        <th>Type</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    ${auditEntries.map(entry => {
                        const isRedFlag = entry.classification === 'Motion' && (!entry.decision_made.includes('Yes') || entry.deadline === 'No deadline set');
                        const rowClass = isRedFlag ? 'red-flag' : (entry.classification === 'Motion' ? 'motion' : 'movement');
                        return `
                        <tr class="${rowClass}">
                            <td>${entry.meeting_name}</td>
                            <td>${entry.purpose}</td>
                            <td>${entry.decision_made}</td>
                            <td>${entry.action_owner}</td>
                            <td>${entry.deadline}</td>
                            <td>${entry.classification}</td>
                            <td>${isRedFlag ? '⚠️ RED FLAG' : '✅ Good'}</td>
                        </tr>`;
                    }).join('')}
                </tbody>
            </table>
        </div>
        
        <div class="section">
            <h2>QRM Coaching Recommendations</h2>
            <ul>
                <li><strong>Eliminate Motion Overload:</strong> ${stats.motionPercent}% motion meetings is ${stats.motionPercent > 50 ? 'too high' : 'acceptable'}</li>
                <li><strong>Focus on Decisions:</strong> Ensure every motion meeting produces clear decisions</li>
                <li><strong>Set Hard Deadlines:</strong> No action without specific deadlines</li>
                <li><strong>Apply QRM Principles:</strong> Reduce meeting lead times, implement visible priorities</li>
                <li><strong>Daily Stand-ups:</strong> Replace status meetings with 10-minute daily check-ins</li>
            </ul>
        </div>
        
        <div class="section">
            <h2>Next Steps</h2>
            <p><strong>Contact Jan Pauwels for Strategic Consulting:</strong></p>
            <p>Email: info@smartqrmnavigator.com</p>
            <p>Subject: Meeting Culture Transformation</p>
            <p>Your audit shows ${stats.motionPercent}% motion meetings. Target: Reduce to under 30% for optimal flow.</p>
        </div>
    </body>
    </html>
    `);
    
    newWindow.document.close();
    
    // Trigger print dialog for PDF generation
    setTimeout(() => {
        newWindow.print();
        newWindow.close();
    }, 1000);
  };

  const exportToExcel = () => {
    if (auditEntries.length === 0) {
      alert('Please add at least one meeting to audit before exporting to Excel');
      return;
    }
    
    // Create properly formatted Excel-compatible CSV
    const stats = getMotionMovementStats();
    const reportDate = new Date().toLocaleDateString();
    
    // Header with metadata
    const csvLines = [
      'MEETING CULTURE AUDIT ANALYSIS',
      `Report Date,${reportDate}`,
      `Total Meetings,${auditEntries.length}`,
      `Motion Meetings,${stats.motionPercent}%`,
      `Movement Meetings,${stats.movementPercent}%`,
      '',
      'DETAILED AUDIT DATA',
      'Meeting Name,Date/Time,Participants,Purpose,Decision Made,Action Owner,Deadline,Classification,Red Flag,QRM Coaching Note'
    ];
    
    // Add audit data
    auditEntries.forEach(entry => {
      const isRedFlag = entry.classification === 'Motion' && (!entry.decision_made.includes('Yes') || entry.deadline === 'No deadline set');
      const coachingNote = entry.classification === 'Motion' 
        ? 'Focus on clear decisions and deadlines - apply QRM principles' 
        : 'Good for status sharing - consider daily stand-ups instead';
        
      csvLines.push([
        `"${entry.meeting_name}"`,
        `"${entry.date_time}"`,
        `"${entry.participants}"`,
        `"${entry.purpose}"`,
        `"${entry.decision_made}"`,
        `"${entry.action_owner}"`,
        `"${entry.deadline}"`,
        `"${entry.classification}"`,
        `"${isRedFlag ? 'YES - NEEDS ATTENTION' : 'NO'}"`,
        `"${coachingNote}"`
      ].join(','));
    });
    
    const csvContent = csvLines.join('\n');
    
    // Create Excel-compatible file with BOM for proper encoding
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Meeting_Audit_Analysis_${reportDate.replace(/\//g, '-')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    alert(`📊 Excel file generated with ${auditEntries.length} meetings analyzed! Open in Excel for full formatting.`);
  };

  const getMotionMovementStats = () => {
    const motion = auditEntries.filter(e => e.classification === 'Motion').length;
    const movement = auditEntries.filter(e => e.classification === 'Movement').length;
    const total = motion + movement;
    
    return {
      motionPercent: total > 0 ? Math.round((motion / total) * 100) : 0,
      movementPercent: total > 0 ? Math.round((movement / total) * 100) : 0,
      totalMeetings: total,
      decisionsPerWeek: Math.round(motion / (total > 0 ? 1 : 1)) // Simplified calculation
    };
  };

  const stats = getMotionMovementStats();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-16">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-16 mt-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Cut the Fog: <span className="text-blue-600">Meeting Culture Audit</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transform your meeting culture by distinguishing motion (activity without progress) 
              from movement (decisions with deadlines)
            </p>
          </div>

          {/* Enhanced Bezos Story & Philosophy */}
          <Card className="p-8 mb-12 bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Why Cut the Fog?</h2>
            
            {/* Bezos Amazon Story */}
            <div className="bg-white p-6 rounded-lg border border-yellow-300 mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Jeff Bezos Banned PowerPoint from Amazon Meetings</h3>
              <div className="text-gray-700 space-y-4 leading-relaxed">
                <p><strong>Why?</strong> Because slides were hiding a deeper problem: Teams were mistaking motion for movement.</p>
                
                <p>In 2004, Amazon meetings were filled with flashy decks and endless updates. 
                Everyone felt productive. Nothing actually got done.</p>
                
                <p><strong>That's when Bezos made the shift:</strong><br/>
                • No more slides<br/>
                • No more showmanship<br/>
                • Every meeting now starts with a silent reading of a 6-page narrative</p>
                
                <p><strong>The result?</strong><br/>
                • Decisions got faster<br/>
                • Projects stopped stalling<br/>
                • One team cut meeting time from 30 to 12 hours - while doubling output</p>
              </div>
            </div>

            {/* Motion vs Movement Examples */}
            <div className="bg-white p-6 rounded-lg border border-yellow-300 mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Motion vs Movement: The Critical Difference</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold text-red-600 mb-3">🔄 MOTION (Expensive Theater)</h4>
                  <ul className="text-gray-700 space-y-2 text-sm">
                    <li>• Creating a 50-slide strategy deck</li>
                    <li>• Status meeting with 12 people updating each other</li>
                    <li>• "Planning to plan to plan"</li>
                    <li>• Writing about work instead of doing it</li>
                    <li>• Tracking activity instead of outcomes</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-green-600 mb-3">🎯 MOVEMENT (Real Progress)</h4>
                  <ul className="text-gray-700 space-y-2 text-sm">
                    <li>• Making 3 decisions with clear owners and deadlines</li>
                    <li>• Session that unblocks one key deliverable</li>
                    <li>• Launching a prototype by Friday</li>
                    <li>• Doing work, not discussing it</li>
                    <li>• Measuring outcomes, not activities</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Fortune 500 Case Study */}
            <div className="bg-white p-6 rounded-lg border border-yellow-300 mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Real Case: Fortune 500 Transformation</h3>
              <div className="text-gray-700 space-y-3">
                <p><strong>The Problem:</strong><br/>
                • 47 transformation workstreams<br/>
                • Hundreds of consultants<br/>
                • Thousands of tracked KPIs<br/>
                • Zero business results</p>
                
                <p><strong>The Solution:</strong><br/>
                • Killed 44 workstreams<br/>
                • Focused on 3<br/>
                • Made more progress in 90 days than they had in 3 years</p>
              </div>
            </div>

            {/* The Truth About Motion */}
            <div className="bg-gradient-to-r from-red-50 to-pink-50 p-6 rounded-lg border border-red-200">
              <h3 className="text-xl font-bold text-red-800 mb-4">💡 Want to Spot the Fog?</h3>
              <div className="text-red-700 space-y-2 text-sm">
                <p><strong>If a meeting ends with "let's schedule another meeting," it's motion.</strong></p>
                <p><strong>If you're writing about work instead of doing it, it's motion.</strong></p>
                <p><strong>If you're tracking activity instead of outcomes, it's motion.</strong></p>
              </div>
              <div className="mt-4 p-4 bg-white rounded border border-red-300">
                <p className="text-gray-800 font-medium">
                  <strong>Cancel the motion. All of it.</strong><br/>
                  When motion stops, movement accelerates.<br/>
                  When meetings shrink, outcomes grow.<br/>
                  Your team doesn't need more meetings. They need more decisions.
                </p>
              </div>
            </div>
          </Card>

          {/* Live Audit Form */}
          <Card className="p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">2. Live Audit Form</h2>
            <p className="text-gray-600 mb-6">
              Audit your meetings directly on this page - no Excel required.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <Label className="text-sm font-medium mb-2 block">Meeting Name *</Label>
                <Select 
                  value={currentEntry.meeting_name}
                  onValueChange={(value) => handleInputChange('meeting_name', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select meeting type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Weekly Status Meeting">Weekly Status Meeting</SelectItem>
                    <SelectItem value="Project Review">Project Review</SelectItem>
                    <SelectItem value="Planning Session">Planning Session</SelectItem>
                    <SelectItem value="Decision Meeting">Decision Meeting</SelectItem>
                    <SelectItem value="Stand-up/Daily">Stand-up/Daily</SelectItem>
                    <SelectItem value="Strategy Meeting">Strategy Meeting</SelectItem>
                    <SelectItem value="Budget Review">Budget Review</SelectItem>
                    <SelectItem value="Performance Review">Performance Review</SelectItem>
                    <SelectItem value="Client Meeting">Client Meeting</SelectItem>
                    <SelectItem value="Other">Other (specify in purpose)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-2 block">Date/Time</Label>
                <Input
                  type="datetime-local"
                  value={currentEntry.date_time}
                  onChange={(e) => handleInputChange('date_time', e.target.value)}
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-2 block">Participants</Label>
                <Select 
                  value={currentEntry.participants}
                  onValueChange={(value) => handleInputChange('participants', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select group size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-3 people">1-3 people</SelectItem>
                    <SelectItem value="4-6 people">4-6 people</SelectItem>
                    <SelectItem value="7-10 people">7-10 people</SelectItem>
                    <SelectItem value="11-15 people">11-15 people</SelectItem>
                    <SelectItem value="16+ people">16+ people (Red flag!)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-2 block">Purpose</Label>
                <Select 
                  value={currentEntry.purpose}
                  onValueChange={(value) => handleInputChange('purpose', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select meeting purpose" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Status update">Status update</SelectItem>
                    <SelectItem value="Approve/Reject decision">Approve/Reject decision</SelectItem>
                    <SelectItem value="Problem solving">Problem solving</SelectItem>
                    <SelectItem value="Planning & prioritization">Planning & prioritization</SelectItem>
                    <SelectItem value="Information sharing">Information sharing</SelectItem>
                    <SelectItem value="Brainstorming">Brainstorming</SelectItem>
                    <SelectItem value="Review & feedback">Review & feedback</SelectItem>
                    <SelectItem value="Team alignment">Team alignment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-2 block">Decision Made</Label>
                <Select 
                  value={currentEntry.decision_made}
                  onValueChange={(value) => handleInputChange('decision_made', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Was a decision made?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes - Clear decision">Yes - Clear decision</SelectItem>
                    <SelectItem value="Partial - Some decisions">Partial - Some decisions</SelectItem>
                    <SelectItem value="Deferred - Need more info">Deferred - Need more info</SelectItem>
                    <SelectItem value="No - Just discussion">No - Just discussion</SelectItem>
                    <SelectItem value="N/A - Info sharing only">N/A - Info sharing only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-2 block">Action Owner</Label>
                <Select 
                  value={currentEntry.action_owner}
                  onValueChange={(value) => handleInputChange('action_owner', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Who owns the action?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Single person assigned">Single person assigned</SelectItem>
                    <SelectItem value="Team assigned">Team assigned</SelectItem>
                    <SelectItem value="Multiple people (unclear)">Multiple people (unclear)</SelectItem>
                    <SelectItem value="No owner assigned">No owner assigned</SelectItem>
                    <SelectItem value="N/A - No action needed">N/A - No action needed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-2 block">Deadline</Label>
                <Select 
                  value={currentEntry.deadline}
                  onValueChange={(value) => handleInputChange('deadline', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="When is the deadline?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Within 1 week">Within 1 week</SelectItem>
                    <SelectItem value="Within 2 weeks">Within 2 weeks</SelectItem>
                    <SelectItem value="Within 1 month">Within 1 month</SelectItem>
                    <SelectItem value="Beyond 1 month">Beyond 1 month</SelectItem>
                    <SelectItem value="No deadline set">No deadline set</SelectItem>
                    <SelectItem value="N/A - No action">N/A - No action</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-2 block">Classification *</Label>
                <Select 
                  value={currentEntry.classification}
                  onValueChange={(value) => handleInputChange('classification', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Motion or Movement?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Motion">🎯 Motion (Decisions & Actions)</SelectItem>
                    <SelectItem value="Movement">📊 Movement (Status & Updates)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Motion = Decisions made, Movement = Status shared
                </p>
              </div>
            </div>
            
            {/* Conditional Highlighting */}
            {currentEntry.classification === 'Motion' && (!currentEntry.decision_made || !currentEntry.deadline) && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">
                  ⚠️ <strong>Motion meetings require decisions and deadlines!</strong> 
                  {!currentEntry.decision_made && " Missing decision."}
                  {!currentEntry.deadline && " Missing deadline."}
                </p>
              </div>
            )}
            
            <div className="flex space-x-4">
              <Button 
                onClick={addAuditEntry}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                Add Meeting to Audit
              </Button>
              
              {auditEntries.length > 0 && (
                <div className="flex flex-wrap gap-3">
                  <Button 
                    variant="outline"
                    onClick={exportToPDF}
                    className="bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                  >
                    📄 Generate Executive PDF Report
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={exportToExcel}
                    className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                  >
                    📊 Export Detailed Excel Analysis
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={() => setShowDashboard(!showDashboard)}
                    className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                  >
                    📈 {showDashboard ? 'Hide' : 'Show'} Dashboard
                  </Button>
                </div>
              )}
            </div>
          </Card>

          {/* Visual Dashboard */}
          {showDashboard && auditEntries.length > 0 && (
            <Card className="p-8 mb-12 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">📈 Meeting Culture Dashboard</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="text-center p-6 bg-white rounded-lg shadow-sm">
                  <div className="text-3xl font-bold text-blue-600 mb-2">{stats.motionPercent}%</div>
                  <div className="text-sm text-gray-600">Motion Meetings</div>
                  <div className="text-xs text-gray-500">(Decisions & Actions)</div>
                </div>
                
                <div className="text-center p-6 bg-white rounded-lg shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">{stats.movementPercent}%</div>
                  <div className="text-sm text-gray-600">Movement Meetings</div>
                  <div className="text-xs text-gray-500">(Status & Updates)</div>
                </div>
                
                <div className="text-center p-6 bg-white rounded-lg shadow-sm">
                  <div className="text-3xl font-bold text-purple-600 mb-2">{stats.decisionsPerWeek}</div>
                  <div className="text-sm text-gray-600">Decisions Per Week</div>
                  <div className="text-xs text-gray-500">(Decision Density)</div>
                </div>
              </div>
            </Card>
          )}

          {/* Audit Results */}
          {auditEntries.length > 0 && (
            <Card className="p-8 mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Meeting Audit Results</h2>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-300 p-3 text-left">Meeting</th>
                      <th className="border border-gray-300 p-3 text-left">Purpose</th>
                      <th className="border border-gray-300 p-3 text-left">Decision</th>
                      <th className="border border-gray-300 p-3 text-left">Deadline</th>
                      <th className="border border-gray-300 p-3 text-left">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditEntries.map((entry) => (
                      <tr key={entry.id} className={`${
                        entry.classification === 'Motion' && (!entry.decision_made || !entry.deadline) 
                          ? 'bg-red-50' 
                          : entry.classification === 'Motion' 
                            ? 'bg-green-50' 
                            : 'bg-blue-50'
                      }`}>
                        <td className="border border-gray-300 p-3">{entry.meeting_name}</td>
                        <td className="border border-gray-300 p-3">{entry.purpose}</td>
                        <td className="border border-gray-300 p-3">{entry.decision_made || '-'}</td>
                        <td className="border border-gray-300 p-3">{entry.deadline || '-'}</td>
                        <td className="border border-gray-300 p-3">
                          <Badge className={entry.classification === 'Motion' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}>
                            {entry.classification === 'Motion' ? '🎯 Motion' : '📊 Movement'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Call to Action */}
          <Card className="p-8 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">Audit Your Calendar. Accelerate Your Flow.</h2>
            <p className="text-xl mb-8">
              Ready to transform your meeting culture and boost manufacturing performance?
            </p>
            
            <div className="space-x-4">
              <Button 
                size="lg" 
                className="px-8 py-3 bg-white text-blue-600 hover:bg-blue-50 font-semibold"
                onClick={() => window.location.href = '/business-case'}
              >
                <BarChart3 className="mr-2 h-5 w-5" />
                Calculate QRM ROI
              </Button>
              <Button 
                variant="outline"
                size="lg" 
                className="px-8 py-3 border-2 border-white text-white hover:bg-white hover:text-blue-600 font-semibold"
                onClick={() => window.open('mailto:info@smartqrmnavigator.com?subject=Cut the Fog - Meeting Culture Consultation&body=Hello Jan,\n\nI completed the Cut the Fog meeting audit and want to discuss transforming our meeting culture.\n\nOur audit results:\n• Total meetings audited: ' + stats.totalMeetings + '\n• Motion meetings: ' + stats.motionPercent + '%\n• Movement meetings: ' + stats.movementPercent + '%\n• Decisions per week: ' + stats.decisionsPerWeek + '\n\nI need help with:\n• Reducing motion meetings\n• Improving decision density\n• Accelerating our manufacturing flow\n\nCompany: [Your Company]\nName: [Your Name]\nEmail: [Your Email]\n\nReady to cut the fog!\n\nBest regards', '_blank')}
              >
                <Award className="mr-2 h-5 w-5" />
                Get Jan Pauwels Coaching
              </Button>
            </div>
          </Card>

          {/* Navigation */}
          <div className="text-center mt-12">
            <Button 
              variant="outline"
              onClick={() => window.location.href = '/'}
              className="px-8 py-3"
            >
              ← Back to Home
            </Button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default CutTheFogPage;
